import { EventEmitter, Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Router } from '@angular/router'
import { httpConfig } from './httpConfig'
import { CookieService } from 'ngx-cookie-service'
import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { filter, switchMap, catchError, take } from 'rxjs/operators'
import { NgZone } from '@angular/core'
import { MySharedService } from '../helpers/MySharedService'
import { AlertController, Platform } from '@ionic/angular'
import { NativeBiometric } from 'capacitor-native-biometric'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private connString: any = this.myHTTPrequests.GetConnURL()
  private refreshTokenInProgress = false // Track whether a token refresh is in progress
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  ) // Token update notifier

  constructor(
    private http: HttpClient,
    private router: Router,
    private myHTTPrequests: httpConfig,
    private cookieService: CookieService,
    private zone: NgZone,
    private myService: MySharedService,
    private alertController: AlertController,
    private platform: Platform
  ) {}

  private async presentAlert(
    header: string,
    message: string,
    showStatusButton: boolean = false
  ) {
    const buttons: any[] = [
      {
        text: 'OK',
        role: 'cancel',
      },
    ]

    if (showStatusButton) {
      buttons.push({
        text: 'Check Status',
        cssClass: 'status-button',
        handler: () => {
          window.open('https://status.supabase.com/', '_blank')
        },
      })
    }

    const alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: buttons,
    })
    await alert.present()
  }

  login(username: string, password: string) {
    return this.http
      .post<{
        accessToken: string
        refreshToken: string
        username: string
        profilePicture: any
        id: any
        subscriptionType: any
        userType: any
        refreshTokenExpiration: any
      }>(`${this.connString}login`, {
        UserName: username,
        Password: password,
      })
      .toPromise()
      .then(async (response) => {
        // Set Login status on profile page
        this.myService.setLoginStatus('Success')
        // TODO: Store the tokens in HttpOnly cookies - more secure
        this.setToken('accessToken', response.accessToken)
        this.setToken('refreshToken', response.refreshToken)
        this.setToken('refreshTokenExpiration', response.refreshTokenExpiration)

        localStorage.setItem('accessToken', response.accessToken)
        localStorage.setItem('refreshToken', response.refreshToken)
        localStorage.setItem(
          'refreshTokenExpiration',
          response.refreshTokenExpiration
        )
        localStorage.setItem('userName', response.username)
        localStorage.setItem('profilePicture', response.profilePicture)
        localStorage.setItem('id', response.id)
        localStorage.setItem('subscriptionType', response.subscriptionType)
        localStorage.setItem('userType', response.userType)

        this.updateProfilePicture(response.profilePicture)
        this.updateLogoutStatus('true')
        this.router.navigate(['/loged-in'], { replaceUrl: true })

        this.presentAlert(
          'Success',
          `Login Successfully, welcome ${response.username}!`
        )
        return response
      })
      .catch((err: HttpErrorResponse) => {
        this.zone.run(() => {
          let errorMessage = 'Login failed: ' + (err.error?.text || err.message)

          this.myService.setLoginStatus('Failed')

          if (
            err.error?.text == 'Invalid username or password.' ||
            err.error == 'Invalid username or password.'
          ) {
            this.presentAlert('Login Failed', 'Invalid username or password.')
          } else if (err['status'] == 500) {
            this.presentAlert(
              'Database Offline',
              'Apologies, it seems our database is currently experiencing an outage. Our technicians have been notified. You can monitor the real-time status of our service provider (Supabase) below.',
              true
            )
          } else {
            this.presentAlert(
              'Login Failed',
              err.error || 'An error occurred during login.'
            )
          }
        })
        throw err
      })
  }

  // Get token from HttpOnly cookie
  // Fetch access token from localStorage or cookie
  getAccessToken(): string {
    return (
      localStorage.getItem('accessToken') ||
      this.cookieService.get('accessToken') ||
      'null'
    )
  }

  // Fetch refresh token from localStorage or cookie
  getRefreshToken(): string {
    return (
      localStorage.getItem('refreshToken') ||
      this.cookieService.get('refreshToken') ||
      'null'
    )
  }

  getTokenExpiration(): any {
    return (
      localStorage.getItem('refreshTokenExpiration') ||
      this.cookieService.get('refreshTokenExpiration') ||
      'null'
    )
  }

  setToken(key: string, token: string) {
    this.cookieService.set(key, token, { secure: true, sameSite: 'Lax' })
  }

  // Refresh the access token using the refresh token
  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken()
    const accessToken = this.getAccessToken()
    const userName = this.getUsername() // Get username

    if (
      refreshToken === 'null' ||
      accessToken === 'null' ||
      userName === 'null' ||
      !refreshToken ||
      !accessToken ||
      !userName
    ) {
      this.logout() // Force logout if we can't refresh
      return throwError('Missing tokens or username')
    }

    // If a refresh is already in progress, wait for the existing one
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          if (token === 'error') {
            return throwError('Refresh failed')
          }
          return of(token)
        })
      )
    }

    // Set refresh process flag to true
    this.refreshTokenInProgress = true
    this.refreshTokenSubject.next(null)

    // Perform the refresh request
    return this.http
      .post<{
        accessToken: string
        refreshToken: string
        refreshTokenExpiration: any
      }>(
        `${this.connString}refresh-token`,
        {
          RefreshToken: refreshToken,
          Username: userName, // Matched to backend 'tokenRequest.Username'
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        switchMap((response) => {
          // Store new tokens
          this.setToken('accessToken', response.accessToken)
          this.setToken('refreshToken', response.refreshToken)
          this.setToken(
            'refreshTokenExpiration',
            response.refreshTokenExpiration
          )

          localStorage.setItem(
            'refreshTokenExpiration',
            response.refreshTokenExpiration
          )
          localStorage.setItem('accessToken', response.accessToken)
          localStorage.setItem('refreshToken', response.refreshToken)

          // Notify that refresh is complete
          this.refreshTokenSubject.next(response.accessToken)
          this.refreshTokenInProgress = false

          // Return the new access token
          return of(response.accessToken)
        }),
        catchError((error) => {
          this.refreshTokenInProgress = false
          this.refreshTokenSubject.next('error') // Notify waiters of failure

          if (error instanceof HttpErrorResponse && error.status === 401) {
            this.logout()
          }

          return throwError(error)
        })
      )
  }

  // Check if the token is expired
  isTokenExpired(token: string): boolean {
    if (!token || token === 'null' || token === '') return true

    try {
      const parts = token.split('.')
      if (parts.length !== 3) return true // Not a valid JWT

      const tokenPayload = JSON.parse(atob(parts[1]))
      const expiryTime = tokenPayload.exp * 1000 // Convert to milliseconds

      if (!expiryTime) return true

      // Buffer of 10 seconds to avoid edge cases
      return Date.now() > expiryTime - 10000
    } catch (e) {
      console.error('Error checking token expiration:', e)
      return true
    }
  }

  isRefreshTokenExpired(): boolean {
    const expiration = this.getTokenExpiration()
    if (!expiration || expiration === 'null' || expiration === '') return true

    try {
      // Ensure we handle the date string correctly
      const expDate = new Date(expiration)
      const now = new Date()

      if (isNaN(expDate.getTime())) {
        console.warn(
          'Invalid refresh token expiration date format:',
          expiration
        )
        return true
      }

      // Buffer of 1 minute for refresh token
      return now.getTime() > expDate.getTime() - 60000
    } catch (e) {
      console.error('Error checking refresh token expiration:', e)
      return true
    }
  }

  // Auto-login check
  autoLogin() {
    if (this.isAuthenticated()) {
      const currentRoute = this.router.url
      // Only redirect if we are on a "guest" page or the root
      if (
        currentRoute === '/' ||
        currentRoute === '/home' ||
        currentRoute === '/profilepage' ||
        currentRoute === '/default-page'
      ) {
        this.router.navigate(['/loged-in'], { replaceUrl: true })
      }
    }
  }

  async registerUser(email: string, password: string, userType: string) {
    return this.http
      .post(`${this.connString}register`, {
        Email: email,
        Password: password,
        Type: userType,
      })
      .toPromise()
      .then((response) => {
        this.presentAlert(
          'Registration Successful',
          'Please check your email for the verification code. Then click on VERIFY EMAIL NOW button below.'
        )
        return response
      })
      .catch((err) => {
        if (err.error) {
          this.presentAlert('Registration Failed', err.error)
        } else if (err['status'] == 500) {
          this.presentAlert(
            'Server Error',
            'Apologies, it seems our database is offline. Our Technicians has been notified. We apologize for the inconvenience and we will get you back online ASAP!'
          )
        } else {
          this.presentAlert(
            'Registration Failed',
            'An error occurred during registration.'
          )
        }
        throw err
      })
  }

  async verifyEmail(email: string, verificationCode: string) {
    return this.http
      .post(`${this.connString}verify-email`, {
        Email: email,
        VerificationCode: verificationCode,
      })
      .toPromise()
      .then((response) => {
        this.presentAlert(
          'Success',
          'Email verification successful. Please login to access your profile.'
        )
      })
      .catch((err: any) => {
        if (err.error && typeof err.error === 'string') {
          this.presentAlert('Verification Failed', err.error)
        } else if (err.error && err.error.message) {
          this.presentAlert('Verification Failed', err.error.message)
        } else if (err.status === 400) {
          this.presentAlert(
            'Invalid Code',
            'Incorrect Verification code or Invalid Request. Please try again.'
          )
        } else {
          this.presentAlert('Error', 'An error occurred during verification.')
        }
      })
  }

  getToken() {
    return localStorage.getItem('accessToken') || 'null'
  }

  getSubscriptionType(): string {
    return localStorage.getItem('subscriptionType') || 'normal' // Default to 'normal' if not found
  }

  getProfilePicture(): string {
    return localStorage.getItem('profilePicture') || 'default-profile-pic-url'
  }

  getUserType(): string {
    return localStorage.getItem('userType') || 'NormalUser'
  }

  // Observable to hold the profile picture URL
  profilePictureChanged = new EventEmitter<string>()

  updateProfilePicture(newProfilePicture: string) {
    localStorage.setItem('profilePicture', newProfilePicture)
    this.profilePictureChanged.emit(newProfilePicture) // Emit the event
  }

  profileSubscriptionChanged = new EventEmitter<string>()

  updateProfileSubscription(newProfileSubscription: string) {
    localStorage.setItem('subscriptionType', newProfileSubscription)
    this.profileSubscriptionChanged.emit(newProfileSubscription) // Emit the event
  }

  // Logout change
  logoutChange = new EventEmitter<string>()

  updateLogoutStatus(logoutStatus: string) {
    localStorage.setItem('logoutStatus', logoutStatus)
    this.logoutChange.emit(logoutStatus) // Emit the event
  }

  getUserId() {
    return localStorage.getItem('id')
  }

  getUsername() {
    return localStorage.getItem('userName')
  }

  adminPage() {
    this.router.navigate(['/admin'], { replaceUrl: true })
  }

  logout() {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('refreshTokenExpiration')
    localStorage.removeItem('userName')
    localStorage.removeItem('profilePicture')
    localStorage.removeItem('id')
    localStorage.removeItem('subscriptionType')
    localStorage.removeItem('userType')
    this.updateProfilePicture('')
    this.updateProfileSubscription('')
    this.updateLogoutStatus('false')

    this.cookieService.delete('accessToken')
    this.cookieService.delete('refreshToken')
    this.cookieService.delete('refreshTokenExpiration')

    this.router.navigate(['/profilepage'], { replaceUrl: true })
  }

  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken()
    const refreshToken = this.getRefreshToken()

    if (accessToken === 'null' || refreshToken === 'null') {
      return false
    }

    if (this.isRefreshTokenExpired()) {
      this.logout()
      return false
    }

    return true
  }

  // Forgot password

  // Method to request a password reset (sends verification code)
  requestPasswordReset(email: string) {
    return this.http
      .post(`${this.connString}forgot-password`, { Email: email })
      .toPromise()
      .then((response) => {
        this.presentAlert('Success', 'Verification code sent.')
      })
      .catch((err) => {
        // Handle error silently or with alert
      })
  }

  // Method to reset the password using the verification code
  resetPassword(email: string, verificationCode: string, newPassword: string) {
    return this.http
      .post(`${this.connString}reset-password`, {
        Email: email,
        VerificationCode: verificationCode,
        NewPassword: newPassword,
      })
      .toPromise()
      .then((response) => {
        this.presentAlert('Success', 'Password reset successful.')
      })
      .catch((err) => {
        if (err['status'] == 400) {
          this.presentAlert(
            'Error',
            'Incorrect Verification code. Please try again...'
          )
        }
      })
  }
}
